import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v7';
import type { ReactElement } from 'react';
import { Toaster } from 'sonner';
import { useInitAuth } from '@/features/auth/hooks/useInitAuth';
import { AppRouter } from '@/router';

/**
 * App composition root.
 *
 * Four top-level responsibilities, in order:
 *
 *   1. Toaster (sonner) — anchored to the body so route changes
 *      don't unmount it. Mounted OUTSIDE the QueryClientProvider so
 *      a server-initiated toast (e.g. global 401 notifier) can fire
 *      before any route is ready.
 *
 *   2. QueryClientProvider — single react-query instance for the
 *      whole app. Default options align with TanStack Query's idle
 *      defaults but pin `refetchOnWindowFocus` to keep the UI in
 *      sync after a user alt-tabs back.
 *
 *   3. NuqsAdapter — URL search-param state. Used by every catalog
 *      page via `useCatalogFilters` to keep filter/sort/page state
 *      shareable via link and back-button-clean. Sits between the
 *      QueryClient and the Router so the hook context is available
 *      inside route components while still inside react-query's
 *      cache lifecycle.
 *
 *   4. AppRouter — the actual route tree. Wrapped in <AppRoot> so
 *      useInitAuth (which uses useEffect) can run inside the router
 *      tree.
 */
function makeQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        refetchOnWindowFocus: true,
        retry: 1,
      },
      mutations: {
        retry: false,
      },
    },
  });
}

function AppRoot(): ReactElement {
  useInitAuth();
  return <AppRouter />;
}

export function App(): ReactElement {
  const queryClient = makeQueryClient();
  return (
    <QueryClientProvider client={queryClient}>
      <NuqsAdapter>
        <AppRoot />
      </NuqsAdapter>
      <Toaster
        position="top-right"
        richColors
        closeButton
        toastOptions={{
          duration: 4000,
          classNames: {
            toast: 'rounded-lg shadow-lg border border-border',
          },
        }}
      />
    </QueryClientProvider>
  );
}
